<template>
  <div class="space-y-2">
    <div class="brand-card-lg">
      <p class="title">Your Account</p>
      <p class="subtitle mt-3 text-brand-brown text-lg font-medium">Adjust account and security details.</p>
      <div class="flex flex-col mt-4 brand-input">
        <div class="-mx-3 md:flex">
          <div class="md:w-1/2 px-3 mb-6">
            <p class="form-label mb-2">Name</p>
            <el-input v-model="name" type="text"></el-input>
          </div>
        </div>

        <div class="-mx-3 md:flex">
          <div class="md:w-1/2 px-3">
            <p class="form-label mb-2">Email</p>
            <el-input v-model="email" type="text"></el-input>
            <p v-if="!isEmailConfirmed" class="text-brand-red text-sm mt-1">Email not yet verified</p>
          </div>

          <div class="md:w-1/2 px-3 mt-7">
            <el-tooltip effect="dark" placement="right" popper-class="brand-tooltip" v-if="isEmailConfirmed">
              <div slot="content">Your email address has been verified.</div>
              <font-awesome-icon :icon="['fas', 'check-circle']" class="text-brand-blue mt-2" size="lg" />
            </el-tooltip>
            <el-button v-else type="primary" :disabled="emailSent" @click="confirmEmail">Resend Verification</el-button>
          </div>
        </div>
      </div>
      <div class="">
        <div class="flex justify-start w-1/2 flex-grow-0 mt-6 space-x-4">
          <el-button @click="save" :loading="loading" type="primary" :disabled="!accountDetailsDirty && !loading">Save Account Details</el-button>
          <el-button @click="reset" type="text" v-if="accountDetailsDirty && !loading">Discard Changes</el-button>
        </div>
      </div>
    </div>

    <div class="brand-card-lg">
      <p class="title">Authentication</p>
      <p class="subtitle text-brand-brown text-lg font-medium">Change your account's password.</p>
      <div class="md:w-1/2 mt-4" v-if="isToggled">
        <p class="tracking-wide text-gray-600 text-sm font-bold mb-1">New Password</p>
        <el-input class="mb-6" show-password v-model="password" type="text"></el-input>
        <p class="tracking-wide text-gray-600 text-sm font-bold mb-1">Confirm Password</p>
        <el-input class="mb-2" show-password v-model="passwordMatch" type="text"></el-input>
      </div>
      <el-button v-if="!isToggled" plain class="mt-4" @click="isToggled = !isToggled">Change Password</el-button>
      <el-button v-if="isToggled" plain class="mt-4" @click="isToggled = false">Cancel</el-button>
      <el-button v-if="isToggled" class="mt-4" @click="setPassword" type="primary">Update Password</el-button>
    </div>

    <div v-if="apiToken" class="brand-card-lg" v-can-enable="'api_keys:read'">
      <p class="title">Cancel Flow API Key{{ uniqueTestToken ? 's' : '' }}</p>

      <p class="subtitle mt-3 mb-6 text-brand-brown text-lg font-medium">
        This key should be kept private, it's part of verifying your

        <router-link to="/settings/embed" class="text-brand-blue"> Churnkey embed code. </router-link>
      </p>

      <p v-if="true" class="subtitle mb-4">
        Your App ID: <span class="font-semibold">{{ appId }}</span>
      </p>

      <p v-if="uniqueTestToken" class="form-label mb-2">Live Key</p>
      <div class="-mx-3 my-3 md:flex brand-input">
        <div class="md:w-1/2 px-3 mb-6 md:mb-0">
          <el-input v-if="hideToken" v-model="apiToken" type="password" disabled>
            <template #append>
              <el-button @click="copyToken" icon="el-icon-copy-document" plain class="border-none focus:outline-none">Copy</el-button>
            </template>
          </el-input>
          <el-input v-else v-model="apiToken" type="text" readonly>
            <template #append>
              <el-button @click="copyToken" icon="el-icon-copy-document" plain class="border-none focus:outline-none">Copy</el-button>
            </template>
          </el-input>
        </div>

        <div class="md:w-1/2 px-3">
          <el-button v-if="hideToken" @click="hideToken = false" type="primary">View</el-button>
          <el-button v-else @click="hideToken = true" type="primary" plain>Hide</el-button>
        </div>
      </div>

      <p class="form-label mb-2">Test Key</p>
      <div v-if="uniqueTestToken" class="-mx-3 my-3 md:flex">
        <div class="md:w-1/2 px-3 mb-6 md:mb-0">
          <el-input v-if="hideTestToken" v-model="apiTestToken" type="password" disabled>
            <template #append>
              <el-button @click="copyTestToken" icon="el-icon-copy-document" plain class="border-none focus:outline-none">Copy</el-button>
            </template>
          </el-input>
          <el-input v-else v-model="apiTestToken" type="text" readonly>
            <template #append>
              <el-button @click="copyTestToken" icon="el-icon-copy-document" plain class="border-none focus:outline-none">Copy</el-button>
            </template>
          </el-input>
        </div>

        <div class="md:w-1/2 px-3">
          <el-button v-if="hideTestToken" @click="hideTestToken = false" type="primary">View</el-button>
          <el-button v-else @click="hideTestToken = true" type="primary" plain>Hide</el-button>
        </div>
      </div>

      <div v-else class="my-3">
        <div>
          <p class="text-sm text-gray-600 font-medium">
            If you would like to use different API keys for test and live mode, you can generate a separate, unique API key for test mode.
          </p>
          <el-button type="text" @click="regenerateTestKey(true)" :loading="loadingKeys"
            ><span class="font-semibold">Generate Unique Test API Key</span></el-button
          >
        </div>
      </div>

      <div class="-mx-3 my-4 md:flex">
        <div class="md:w-1/2 px-3">
          <p class="form-label mb-2">Key Rotation</p>

          <p class="text-sm text-gray-600 mt-1 mb-3">When your API key is regenerated, old API keys will be invalidated</p>

          <el-button type="danger" class="font-bold" @click="regenerateWarning = true">Regenerate</el-button>
        </div>
      </div>

      <el-dialog
        class="p-4"
        custom-class="w-full md:w-3/4 xl:w-1/2 2xl:w-1/3"
        :visible.sync="regenerateWarning"
        :close-on-click-modal="true"
        :append-to-body="true"
      >
        <div class="has-border-radius-16">
          <div class="max-w-md mx-auto my-2 text-brand-brown font-semibold">
            <h3 class="px-5 text-xl leading-6 my-6 font-bold text-gray-900">Are you sure?</h3>
            <p class="px-5 break-normal">
              Regenerating your API key will invalidate old API keys used by your account. You will need to update the API key being used on your
              servers.
            </p>
          </div>
          <div slot="footer" class="dialog-footer mt-8 border-t-2 bg-brand-sand">
            <div class="max-w-md mx-auto px-5 py-5 md:px-0 flex justify-between">
              <el-button @click="regenerateWarning = false">Not Now</el-button>
              <el-button @click="regenerateBothKeys" type="danger" :loading="loadingKeys">Regenerate API Key</el-button>
            </div>
          </div>
        </div>
      </el-dialog>
    </div>

    <div class="brand-card-lg" v-can-enable="'api_keys:read'">
      <p class="title">Data API Keys</p>

      <p class="subtitle mt-3 mb-4 text-brand-brown text-lg font-medium">
        Use our data API to access your data programatically in JSON format. You can use this to build your own custom data integrations.
      </p>

      <p v-if="hasDataApiKey" class="subtitle mb-4">
        Your App ID: <span class="font-semibold">{{ appId }}</span>
      </p>

      <div v-if="hasDataApiKey">
        <p class="form-label mb-2">Live Key</p>
        <div class="-mx-3 my-3 md:flex">
          <div class="md:w-1/2 px-3 mb-6 md:mb-0 brand-input">
            <el-input v-if="hideDataToken" v-model="dataApiToken" type="password" disabled>
              <template #append>
                <el-button @click="copyDataToken" icon="el-icon-copy-document" plain class="border-none focus:outline-none">Copy</el-button>
              </template>
            </el-input>
            <el-input v-else v-model="dataApiToken" type="text" readonly>
              <template #append>
                <el-button @click="copyDataToken" icon="el-icon-copy-document" plain class="border-none focus:outline-none">Copy</el-button>
              </template>
            </el-input>
          </div>

          <div class="md:w-1/2 px-3">
            <el-button v-if="hideDataToken" @click="hideDataToken = false" type="primary">View</el-button>
            <el-button v-else @click="hideDataToken = true" type="primary" plain>Hide</el-button>
          </div>
        </div>

        <p class="form-label mb-2">Test Key</p>
        <div class="-mx-3 my-3 md:flex">
          <div class="md:w-1/2 px-3 mb-6 md:mb-0 brand-input">
            <el-input v-if="hideTestDataToken" v-model="dataApiTestToken" type="password" disabled>
              <template #append>
                <el-button @click="copyTestDataToken" icon="el-icon-copy-document" plain class="border-none focus:outline-none">Copy</el-button>
              </template>
            </el-input>
            <el-input v-else v-model="dataApiTestToken" type="text" readonly>
              <template #append>
                <el-button @click="copyTestDataToken" icon="el-icon-copy-document" plain class="border-none focus:outline-none">Copy</el-button>
              </template>
            </el-input>
          </div>

          <div class="md:w-1/2 px-3">
            <el-button v-if="hideTestDataToken" @click="hideTestDataToken = false" type="primary">View</el-button>
            <el-button v-else @click="hideTestDataToken = true" type="primary" plain>Hide</el-button>
          </div>
        </div>
      </div>
      <!-- Show when no data API key available -->
      <div v-else-if="dataApiEnabled">
        <el-button type="primary" @click="regenerateBothDataKeys()" :loading="loadingKeys">Generate Data API Key</el-button>
      </div>
      <div v-else>
        <p class="text-sm text-gray-600 mt-1 mb-3">
          Note: A data API is not yet set up for your account. Please contact us at support@churnkey.co to talk to us about access.
        </p>
      </div>

      <div v-if="hasDataApiKey" class="-mx-3 my-4 md:flex">
        <div class="md:w-1/2 px-3">
          <p class="form-label mb-2">Key Rotation</p>

          <p class="text-sm text-gray-600 mt-1 mb-3">When your Data API key is regenerated, old Data API keys will be invalidated</p>

          <el-button type="danger" class="font-bold" @click="regenerateDataWarning = true">Regenerate</el-button>
        </div>
      </div>

      <el-dialog
        class="p-4"
        custom-class="w-full md:w-3/4 xl:w-1/2 2xl:w-1/3"
        :visible.sync="regenerateDataWarning"
        :close-on-click-modal="true"
        :append-to-body="true"
      >
        <div class="has-border-radius-16">
          <div class="max-w-md mx-auto my-2 text-brand-brown font-semibold">
            <h3 class="px-5 text-xl leading-6 my-6 font-bold text-gray-900">Are you sure?</h3>
            <p class="px-5 break-normal">
              Regenerating your API key will invalidate old Data API keys. You will need to update the Data API key being used on your servers.
            </p>
          </div>
          <div slot="footer" class="dialog-footer mt-8 border-t-2 bg-brand-sand">
            <div class="max-w-md mx-auto px-5 py-5 md:px-0 flex justify-between">
              <el-button @click="regenerateDataWarning = false">Not Now</el-button>
              <el-button @click="regenerateBothDataKeys" type="danger" :loading="loadingKeys">Regenerate Data API Key</el-button>
            </div>
          </div>
        </div>
      </el-dialog>
    </div>

    <ModalRoot />
  </div>
</template>

<script>
import EventBus from '@/helpers/event-bus';
import UserService from '@/api/user.service';
import OrgService from '@/api/org.service';
import ModalRoot from '@/components/Modal/ModalRoot.vue';
import SetPasswordModal from '@/components/Modal/ModalSetPassword.vue';
import { mapGetters } from 'vuex';
import { SYNC_USER_RECORD } from '@/store/action-types';

export default {
  name: 'Account',
  components: {
    ModalRoot,
  },
  data() {
    return {
      name: '',
      email: '',
      apiToken: '',
      apiTestToken: '',
      dataApiToken: '',
      dataApiTestToken: '',
      hideToken: true,
      hideTestToken: true,
      hideDataToken: true,
      hideTestDataToken: true,
      regenerateWarning: false,
      regenerateDataWarning: false,
      loading: true,
      loadingKeys: false,
      emailSent: false,
      isToggled: false,
      password: null,
      passwordMatch: null,
    };
  },
  async created() {
    this.getAccountTokens();

    if (!this.$store.state.user) {
      await this.$store.dispatch(SYNC_USER_RECORD);
    }

    if (!this.$store.state.user) {
      // reload page
      window.location.reload();
    }

    this.name = this.$store.state.user.name;
    this.email = this.$store.state.user.email;

    // sync user org plan
    this.loading = false;
  },

  async mounted() {
    if (this.user && !this.user.hasPassword) {
      this.showPasswordForm();
    }
  },

  computed: {
    ...mapGetters(['verifiedStripeUser', 'verifiedNotStripeUser', 'payingCustomer', 'dataApiEnabled', 'apiUrl', 'appId']),
    accountDetailsDirty() {
      return this.name !== this.user.name || this.email !== this.user.email;
    },
    uniqueTestToken() {
      return this.apiTestToken && this.apiTestToken !== this.apiToken;
    },
    subscription() {
      return this.$store.getters.userOrgSubscription;
    },
    org() {
      return this.$store.state.org;
    },
    user() {
      return this.$store.state.user;
    },
    hasDataApiKey() {
      return this.dataApiToken && this.dataApiToken.length > 0;
    },
    isEmailConfirmed() {
      if (!this.$store.state.user) return false;
      return this.$store.state.user.emailConfirmed;
    },
  },

  methods: {
    openChat(text) {
      EventBus.$emit('openChat', text);
    },
    reset() {
      this.name = this.user.name;
      this.email = this.user.email;
    },
    async copyText(snippet, message) {
      try {
        await this.$copyText(snippet);
        this.$message({
          message,
          type: 'success',
        });
      } catch (e) {
        this.$message({
          message: 'Could not copy to clipboard',
          type: 'error',
        });
      }
    },
    copyToken() {
      this.copyText(this.apiToken, 'API Key Copied to Clipboard');
    },
    copyTestToken() {
      this.copyText(this.apiTestToken, 'Test API Key Copied to Clipboard');
    },
    copyDataToken() {
      this.copyText(this.dataApiToken, 'Data API Key Copied to Clipboard');
    },
    copyTestDataToken() {
      this.copyText(this.dataApiTestToken, 'Test Data API Key Copied to Clipboard');
    },
    async setPassword() {
      // update
      if (this.password !== this.passwordMatch) {
        this.$notify({ title: "The passwords you entered don't match.", type: 'error' });
        return;
      }
      try {
        await UserService.updateSelf({ password: this.password });
        this.$notify({ title: 'Password Updated', type: 'success' });
        this.isToggled = !this.isToggled;
      } catch (error) {
        this.$notify({ title: 'Update Failed', type: 'error' });
      }
    },
    async save() {
      this.loading = true;
      try {
        await UserService.updateSelf({ name: this.name, email: this.email });
        await this.$store.dispatch(SYNC_USER_RECORD);
        this.$notify({ title: 'Account Updated', type: 'success' });
      } catch (error) {
        this.$notify({ title: 'Update Failed', type: 'error' });
        console.log('Error', error);
      }

      this.loading = false;
    },
    async confirmEmail() {
      try {
        await UserService.sendConfirmationEmail(this.user._id);
        this.$notify({ title: 'Confirmation Email sent, please check your email', type: 'success' });
        this.emailSent = true;
      } catch (error) {
        this.$notify({ title: 'Failed to send confirmation email. Please try again in a little while', type: 'error' });
        console.log('Error:', error);
      }
    },
    async getAccountTokens() {
      try {
        const [res, test, data, dataTest] = await Promise.all([
          OrgService.getToken(this.$store.state.user.org),
          OrgService.getTestToken(this.$store.state.user.org),
          OrgService.getDataApiKey(this.$store.state.user.org),
          OrgService.getDataApiTestKey(this.$store.state.user.org),
        ]);
        this.apiToken = res.churnkeySecret;
        this.apiTestToken = test.churnkeySecret;
        this.dataApiToken = data.apiKey;
        this.dataApiTestToken = dataTest.apiKey;
      } catch (error) {
        console.error('Unable to get API token, make sure stripe connection exists');
      }
    },
    ///////////////////
    // CANCEL FLOW API KEYS
    ///////////////////
    async regenerateBothKeys() {
      this.loadingKeys = true;
      await Promise.all([this.regenerateKey(), this.regenerateTestKey()]);
      this.loadingKeys = false;
    },
    async regenerateKey() {
      try {
        const res = await OrgService.regenerateToken(this.$store.state.user.org);
        this.apiToken = res.churnkeySecret;
      } catch (error) {
        console.error('Unable to get regenerate API token, make sure stripe connection exists');
      }

      this.regenerateWarning = false;
    },
    async regenerateTestKey(onlyTest = false) {
      if (onlyTest) this.loadingKeys = true;
      try {
        const res = await OrgService.regenerateTestToken(this.$store.state.user.org);
        this.apiTestToken = res.churnkeySecret;
      } catch (error) {
        console.error('Unable to get regenerate API token, make sure stripe connection exists');
      }

      if (onlyTest) this.loadingKeys = false;
      this.regenerateWarning = false;
    },
    ///////////////////
    // DATA API KEYS
    ///////////////////
    async regenerateBothDataKeys() {
      this.loadingKeys = true;
      await Promise.all([this.regenerateDataKey(), this.regenerateTestDataKey()]);
      this.loadingKeys = false;
    },
    async regenerateDataKey() {
      try {
        const res = await OrgService.regenerateDataApiKey(this.$store.state.user.org);
        this.dataApiToken = res.dataKey;
      } catch (error) {
        console.error('Unable to get regenerate API token, make sure stripe connection exists');
      }

      this.regenerateDataWarning = false;
    },
    async regenerateTestDataKey(onlyTest = false) {
      if (onlyTest) this.loadingKeys = true;
      try {
        const res = await OrgService.regenerateDataApiKeyTest(this.$store.state.user.org);
        this.dataApiTestToken = res.dataKey;
      } catch (error) {
        console.error('Unable to get regenerate API token, make sure stripe connection exists');
      }

      if (onlyTest) this.loadingKeys = false;
      this.regenerateDataWarning = false;
    },
    showPasswordForm() {
      const title = 'Welcome! Get started by creating your password.';
      const userId = this.user._id;
      EventBus.$emit('modal', {
        component: SetPasswordModal,
        props: {
          title,
          note: 'Please create a password',
          action: async function action(credentials) {
            try {
              await UserService.updateSelf(credentials);
              await this.$store.dispatch('SYNC_USER_RECORD');
              this.$notify({ title: 'Password Set.', type: 'success' });
            } catch (error) {
              console.log(error);
              this.$notify({ title: `Password creation failed: ${error}`, type: 'error' });
            }
          },
        },
      });
    },
  },
};
</script>

<style lang="scss">
.el-dialog {
  border-radius: 10px;
  .el-dialog__header {
    padding-top: 0;
  }
  .el-dialog__body {
    padding: 0;
  }
  .dialog-footer {
    border-radius: 0 0 10px 10px;
  }
  .el-dialog__headerbtn {
    display: none;
  }
}
</style>
